import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import { Icon as OldIcon } from '@/components'
import Text from '@/components/atomic/atoms/Text'
import Section from '@/components/atomic/molecules/Section'
import CellIcon from '@/components/cell/atoms/Icon'
import { IconPack } from '@/components/cell/atoms/Icon/enum'
import COLOR from '@/components/cell/designTokens/color'
import { useCookieConsent } from '@/features/cookie-consent/hooks/use-cookie-consent'
import { CookieSettingsModal } from '@/features/cookie-consent/components/cookie-settings-modal'
import { useLocales } from '@/shared/hooks/useLocales'

export type OldIconName = 'youtube' | 'instagram' | 'twitter' | 'line' | 'facebook'

const FooterIcon = ({ name }: { name: IconPack | OldIconName }): JSX.Element => {
  return Object.values(IconPack).includes(name as IconPack) ? (
    <CellIcon name={name as IconPack} width={32} height={32} />
  ) : (
    <OldIcon name={name} size={32} />
  )
}

export interface FooterSectionText {
  value: string
  url?: string
  key?: string
  onClick?: () => void
}

const FooterSection = ({
  title,
  textsList,
  onClick,
}: {
  title: string
  textsList: FooterSectionText[][]
  onClick?: (item: FooterSectionText) => void
}): JSX.Element => {
  return (
    <StyledSection padding={0} hasDivider={false}>
      <Text element="h3" color={COLOR.gray800} type="title4">
        {title}
      </Text>
      <StyledList>
        {textsList.map((texts, textsIndex) => (
          <li key={`section_${textsIndex}`}>
            {texts.map((text, textIndex) =>
              text.url === undefined ? (
                <Text
                  className={text.onClick ? 'cursor-pointer' : ''}
                  key={`section_${textsIndex}_${textIndex}`}
                  element="span"
                  color={COLOR.gray800}
                  type="caption2"
                  onClick={() => text.onClick?.()}
                >
                  {text.value}
                </Text>
              ) : (
                <a
                  key={`section_${textsIndex}_${textIndex}`}
                  href={text.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => onClick?.(text)}
                >
                  <StyledFooterText $isEmphasized={text.key?.includes('privacy')}>{text.value}</StyledFooterText>
                </a>
              ),
            )}
          </li>
        ))}
      </StyledList>
    </StyledSection>
  )
}

const StyledList = styled.ul`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 16px;
  row-gap: 2px;
  padding: 0px;
  margin: 0px;
  list-style: none;
  li {
    white-space: pre;
    margin-bottom: 8px;
    &:last-child {
      margin-bottom: 0px;
    }
  }
`

const StyledFooterText = styled.p<{ $isEmphasized?: boolean }>`
  margin: unset;
  ${({ theme }) => theme.typography.caption2};
  color: ${({ theme }) => theme.colors.gray800};
  ${({ $isEmphasized }) => ($isEmphasized ? 'font-weight: bold; text-decoration: underline;' : '')}
  white-space: normal;
`

const StyledSection = styled(Section)`
  margin-bottom: 21px;
  h3 {
    margin-bottom: 9px;
  }
  a {
    text-decoration: none;
  }
`

export interface Props {
  label?: string
  logo: { url: string; alt: string }
  description: string
  address: string
  aboutTransaction: string
  iconLink: { name: IconPack | OldIconName; url: string; 'aria-label'?: string }[]
  copyright: string
  onClick?: (item: FooterSectionText) => void
  bottomMargin?: string
  bottomPaddingToAdd?: string
}

const GlobalFooter = ({
  label,
  logo,
  address,
  aboutTransaction,
  copyright,
  iconLink,
  description,
  onClick,
  bottomMargin,
  bottomPaddingToAdd = '0px',
}: Props): JSX.Element => {
  const { showSettings, openSettings, closeSettings, saveCustomSettings, consent } = useCookieConsent()
  const { t } = useTranslation()
  const { isLocale } = useLocales()

  const aboutCompanySection = {
    title: t('common.footer.company.title'),
    textsList: [
      [
        {
          value: t('common.footer.inquiry.marketing.text'),
          url: t('common.footer.inquiry.marketing.link'),
          key: 'inquiry.marketing',
        },
      ],
      isLocale.US || isLocale.TH
        ? []
        : [
            {
              value: t('common.footer.inquiry.application.text'),
              url: t('common.footer.inquiry.application.link'),
              key: 'inquiry.application',
            },
          ],
      [
        {
          value: t('common.footer.cs.terms.text'),
          url: t('common.footer.cs.terms.link'),
          key: 'cs.terms',
        },
      ],
      [
        {
          value: t('common.footer.inquiry.recruit.text'),
          url: t('common.footer.inquiry.recruit.link'),
          key: 'inquiry.recruit',
        },
      ],
      [
        {
          value: t('common.footer.cs.location.text'),
          url: t('common.footer.cs.location.link'),
          key: 'cs.location',
        },
      ],
      [
        {
          value: t('common.footer.cs.privacy.text'),
          url: t('common.footer.cs.privacy.link'),
          key: 'cs.privacy',
        },
      ],
      isLocale.US && isLocale.TH
        ? [
            {
              value: 'Your Privacy Choices',
              onClick: () => {
                openSettings()
              },
              key: 'your.privacy.choices',
            },
          ]
        : [],
    ],
  }

  const csSection = {
    title: t('common.footer.cs.title'),
    textsList: [
      [
        {
          value: t('common.footer.cs.email.address'),
          url: t('common.footer.cs.email.link'),
          key: 'cs.email',
        },
      ],
    ],
  }

  return (
    <StyledFooter aria-label={label} $bottomMargin={bottomMargin} $bottomPaddingToAdd={bottomPaddingToAdd}>
      <StyledLogoWrapper>
        <img src={logo.url} alt={logo.alt} />
      </StyledLogoWrapper>

      <FooterSection title={aboutCompanySection.title} textsList={aboutCompanySection.textsList} onClick={onClick} />

      <FooterSection title={csSection.title} textsList={csSection.textsList} onClick={onClick} />

      <Section padding={0} hasDivider={false}>
        <StyledDescription type="caption2" color="#888888" element="p">
          {description}
        </StyledDescription>
        <Text type="caption2" color="#888888" element="address">
          {address}
        </Text>
      </Section>

      <StyledTransactionWrapper>
        <Text type="caption2" color="#888888">
          {aboutTransaction}
        </Text>
      </StyledTransactionWrapper>

      <IconSection padding={0} hasDivider={false}>
        {iconLink.map((item, index) => (
          <a key={`icon_${index}`} href={item.url} target="_blank" rel="noreferrer" aria-label={item['aria-label']}>
            <FooterIcon name={item.name} />
          </a>
        ))}
      </IconSection>

      <Section padding={0} hasDivider={false}>
        <Text type="caption2" color={COLOR.gray800} element="p">
          {copyright}
        </Text>
      </Section>
      <CookieSettingsModal
        isOpen={showSettings}
        onClose={closeSettings}
        onSave={saveCustomSettings}
        currentSettings={consent?.settings}
      />
    </StyledFooter>
  )
}

const StyledLogoWrapper = styled.div`
  width: 86px;
  height: 17.5px;
  margin-bottom: 24px;
`

const StyledFooter = styled.footer<{ $bottomMargin?: string; $bottomPaddingToAdd?: string }>`
  margin-bottom: ${(props) => props.$bottomMargin || ''};
  padding: 30px;
  padding-bottom: ${(props) => `calc(30px + ${props.$bottomPaddingToAdd})`};
  background-color: ${COLOR.gray200};
  border-top: 4px solid ${COLOR.gray400};
  box-sizing: border-box;
`

const StyledDescription = styled(Text)`
  padding-top: 8px;
  padding-bottom: 0px;
  white-space: pre-line;
`

const StyledTransactionWrapper = styled.div`
  margin: 24px 0;
  white-space: pre-line;
`

const IconSection = styled(Section)`
  margin: 9px 0;
  a {
    margin-right: 10px;
    color: #888888;
  }
`

export default GlobalFooter
